import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, Loader2, Receipt, FileText, FolderOpen, Headphones } from 'lucide-react';
import { notificationService, Notification } from '../services/notificationService';
import { formatDateTimeFromDB } from '../utils/dateUtils';

const tipoConfig: Record<string, { icon: any; color: string }> = {
  fatura: { icon: Receipt, color: 'text-purple-500' },
  relatorio: { icon: FileText, color: 'text-brand' },
  documento: { icon: FolderOpen, color: 'text-amber-500' },
  ticket: { icon: Headphones, color: 'text-blue-500' },
};

export function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [naoLidas, setNaoLidas] = useState(0);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const refreshCount = useCallback(async () => {
    try {
      const count = await notificationService.unreadCount();
      setNaoLidas(count);
    } catch (error) {
      console.error('Erro ao buscar contagem de notificações:', error);
    }
  }, []);

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const result = await notificationService.list();
      setNotifications(result.data);
      setNaoLidas(result.naoLidas);
    } catch (error) {
      console.error('Erro ao carregar notificações:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshCount();
    const interval = setInterval(refreshCount, 60000);
    return () => clearInterval(interval);
  }, [refreshCount]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) loadNotifications();
  };

  const handleClickNotification = async (n: Notification) => {
    if (!n.lida) {
      setNaoLidas((c) => Math.max(0, c - 1));
      setNotifications((prev) => prev.map((x) => (x.id === n.id ? { ...x, lida: true } : x)));
      try {
        await notificationService.markRead(n.id);
      } catch (error) {
        console.error('Erro ao marcar notificação como lida:', error);
      }
    }
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  const handleMarkAll = async () => {
    setNotifications((prev) => prev.map((x) => ({ ...x, lida: true })));
    setNaoLidas(0);
    try {
      await notificationService.markAllRead();
    } catch (error) {
      console.error('Erro ao marcar todas como lidas:', error);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={toggle}
        aria-label="Notificações"
        title="Notificações"
        className="relative p-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
      >
        <Bell className="w-5 h-5 text-muted-foreground" />
        {naoLidas > 0 && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-primary rounded-full text-[10px] flex items-center justify-center text-primary-foreground font-bold">
            {naoLidas > 9 ? '9+' : naoLidas}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-card border border-border rounded-lg shadow-lg z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <h4 className="text-sm font-semibold text-foreground">Notificações</h4>
            {naoLidas > 0 && (
              <button
                onClick={handleMarkAll}
                className="flex items-center gap-1 text-xs text-brand hover:opacity-80"
              >
                <Check className="w-3.5 h-3.5" />
                Marcar todas como lidas
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-brand animate-spin" />
              </div>
            ) : notifications.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8 px-4">
                Nenhuma notificação no momento
              </p>
            ) : (
              notifications.map((n) => {
                const cfg = tipoConfig[n.tipo] ?? tipoConfig.ticket;
                const Icon = cfg.icon;
                return (
                  <button
                    key={n.id}
                    onClick={() => handleClickNotification(n)}
                    className={`w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-muted/40 transition-colors border-b border-border/50 ${n.lida ? 'opacity-60' : ''}`}
                  >
                    <span className={`mt-0.5 shrink-0 ${cfg.color}`}>
                      <Icon className="w-4 h-4" />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className={`block text-sm text-foreground ${n.lida ? 'font-normal' : 'font-semibold'}`}>
                        {n.titulo}
                      </span>
                      {n.mensagem && (
                        <span className="block text-xs text-muted-foreground mt-0.5 truncate">
                          {n.mensagem}
                        </span>
                      )}
                      <span className="block text-[11px] text-muted-foreground mt-0.5">
                        {formatDateTimeFromDB(n.createdAt)}
                      </span>
                    </span>
                    {!n.lida && <span className="w-2 h-2 bg-primary rounded-full shrink-0 mt-1.5" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
